import {TableType as PaymentTableType} from "../../containers/payments/PaymentsListColumns";
import {
    csvBookingNumber,
    csvDate,
    csvInvoice,
    csvMoney,
    csvOpenAmount,
    csvPaymentAmount,
    csvPaymentDone,
    csvPaymentMethod,
    csvPaymentType,
    csvPercent,
    csvSepaClearance,
} from "./TableColumnExports";
import {CurrencyShort} from "../renderAmount";
import {mollieMethodLabel} from "../mollieMethodLabels";
import {PaymentProvider} from "../../generated/graphql";
import i18n from "../../services/i18n";

export const paymentValues = (
    key: string,
    row: PaymentTableType,
    index: number,
    currencyShort: CurrencyShort,
    notExportable: string,
    outputFormat?: "pdf" | "csv",
    // eslint-disable-next-line max-params
): string => {
    // console.log("row", row);
    const firstCourseLesson = row.booking.courseLessons[0];
    const attendee = row.booking.attendee;
    const booking = row.booking;
    const bookingNumber = booking.bookingNumber;
    const bookingNumberPrefix = booking.bookingNumberPrefix;
    const canceled = booking.canceled;

    switch (key) {
        case "rowIndex":
            return `${index + 1}`;
        case "bookingNumber":
            // return `${row.booking.bookingNumberPrefix}-${row.booking.bookingNumber}`;
            return csvBookingNumber({
                bookingNumberPrefix,
                bookingNumber,
                canceled,
            });
        case "courseType":
            return `${firstCourseLesson.course.courseType.name}`;
        case "courseNumber":
            return `${firstCourseLesson.course.prefixedCourseNumber}`;
        case "booker":
            return `${attendee?.booker?.firstname} ${attendee?.booker?.lastname}`;
        case "attendee":
            return `${attendee?.firstname} ${attendee?.lastname}`;
        case "city":
            return firstCourseLesson.location.city;
        case "location":
            return firstCourseLesson.location.name;
        case "firstCourseLesson":
            return csvDate(firstCourseLesson.startDateTime, true);
        case "createDate":
            return csvDate(row.createDate, true);
        case "paymentDue":
            return csvDate(row.paymentDue, true);
        case "paymentDone":
            return csvPaymentDone(row.paymentDone);
        case "paymentAmount":
            return csvPaymentAmount(row.paymentAmount, currencyShort, outputFormat);
        case "invoice":
            return csvInvoice(row.invoice);
        case "paymentType":
            return csvPaymentType(row.booking.paymentType);
        case "paymentMethod":
            // Bei Mollie-Zahlungen ist die Buchungs-PaymentMethod (row.paymentMethod) i.d.R. null;
            // die tatsächlich genutzte Methode steckt in mollieMethod (paypal/creditcard/...).
            // Analog zur UI-Spalte (PaymentMethodBadge) exportieren wir dann das Mollie-Label,
            // sonst stünde im CSV bei allen Mollie-Zeilen nur "-".
            if (row.paymentProvider === PaymentProvider.Mollie && row.mollieMethod) {
                return mollieMethodLabel(row.mollieMethod);
            }

            return csvPaymentMethod(row.paymentMethod);
        case "sepaClearance":
            return csvSepaClearance(row.paymentMethod, Boolean(row.sepaClearance));
        case "vat":
            return csvPercent(row.booking.vat);
        case "openAmount":
            return csvOpenAmount(row.openAmount, row.paymentAmount, currencyShort, outputFormat, row.paymentDone);
        case "invoiceDate":
            if (!row.invoice?.updateDate) {
                return "-";
            }

            return csvDate(row.invoice.updateDate, true);
        case "title":
            return row.title ?? "Automatisch";
        // "Zahlungsquelle" — rendert in der UI Mollie/Manuell aus scheduledPayment.paymentProvider.
        case "paymentSource":
            return row.paymentProvider === PaymentProvider.Mollie
                ? i18n.containers.payments.PaymentsListColumns.providerMollie()
                : i18n.containers.payments.PaymentsListColumns.providerManual();
        // "Refund-Status" — analog zur UI-Badge: kein Refund → "-", voll erstattet → Refund-Label,
        // teilweise → Teil-Refund-Label.
        case "refundStatus": {
            const providerRefundedAmount = Number(
                (row as PaymentTableType & {providerRefundedAmount?: number}).providerRefundedAmount ?? 0,
            );

            if (providerRefundedAmount === 0) {
                return "-";
            }

            const refundBase = Number(row.molliePaidAmount ?? row.paymentAmount);

            return providerRefundedAmount >= refundBase
                ? i18n.containers.payments.PaymentsListColumns.refundBadgeFull()
                : i18n.containers.payments.PaymentsListColumns.refundBadgePartial();
        }
        // "Status" — aktiv vs. gelöscht (Tombstone, deletedAt != null), analog TombstoneIndicator.
        case "tombstoneStatus": {
            const deletedAt = (row as PaymentTableType & {deletedAt?: Date | string | null}).deletedAt;

            return deletedAt
                ? i18n.containers.payments.PaymentsListColumns.tombstone.tagDeleted()
                : i18n.containers.payments.PaymentsListColumns.tombstone.tagActive();
        }
        default:
            return notExportable;
    }
};
